import React, { useState, useRef, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import dayjs from "dayjs";
import { BASE_API_URL } from "../../../../utils/consts";
import { isSuperAdmin } from "../../../../utils/permissions";
import "./DashboardAIAnalysis.scss";

interface AIAnalysisProps {
  financeData: any;
  apartmentData: any;
  currency: string;
  locationName?: string;
  locationId?: string;
  period: string;
  periodKey: string; // "2026-05" or "2026-04|2026-05"
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface HistoryItem {
  analysisId: string;
  periodKey: string;
  periodLabel: string;
  locationId: string | null;
  locationName: string | null;
  score: number;
  ocjena: string;
  kratakSazetak: string;
  trend: string;
  generatedAt: string;
  analysisJson: string;
  chatCount: number;
}

interface AnalysisItem { title: string; description: string }
interface Recommendation { priority: "high" | "medium" | "low"; action: string; effect: string }
interface KeyExpense { category: string; amount: number; percentage: number; comment: string }

interface AnalysisResult {
  rating: string;
  score: number;
  summary: string;
  positives: AnalysisItem[];
  warnings: AnalysisItem[];
  recommendations: Recommendation[];
  keyExpenses: KeyExpense[];
  trend: "growth" | "decline" | "stable";
  trendComment: string;
}

const SCORE_COLOR = (s: number) =>
  s >= 75 ? "var(--ai-ok)" : s >= 50 ? "var(--ai-warn)" : "var(--ai-rose)";

// Handles both old (Serbian) and new (English) property names from cached data
const normalizeAnalysis = (raw: any): AnalysisResult => ({
  rating:          raw.rating   ?? raw.ocjena   ?? "good",
  score:           raw.score    ?? 0,
  summary:         raw.summary  ?? raw.kratakSazetak ?? "",
  positives:       (raw.positives ?? raw.pozitivno ?? []).map((p: any) => ({
    title:       p.title   ?? p.naslov ?? "",
    description: p.description ?? p.opis ?? "",
  })),
  warnings:        (raw.warnings ?? raw.upozorenja ?? []).map((w: any) => ({
    title:       w.title   ?? w.naslov ?? "",
    description: w.description ?? w.opis ?? "",
  })),
  recommendations: (raw.recommendations ?? raw.preporuke ?? []).map((r: any) => ({
    priority: r.priority ?? (r.prioritet === "visok" ? "high" : r.prioritet === "srednji" ? "medium" : r.prioritet === "nizak" ? "low" : r.prioritet) ?? "medium",
    action:   r.action   ?? r.akcija  ?? "",
    effect:   r.effect   ?? r.efekt   ?? "",
  })),
  keyExpenses:     (raw.keyExpenses ?? raw.kljucniTrosakovi ?? []).map((k: any) => ({
    category:   k.category   ?? k.kategorija ?? "",
    amount:     k.amount     ?? k.iznos      ?? 0,
    percentage: k.percentage ?? k.procenat   ?? 0,
    comment:    k.comment    ?? k.komentar   ?? "",
  })),
  trend:        raw.trend        ?? "stable",
  trendComment: raw.trendComment ?? raw.trendKomentar ?? "",
});

const RATING_ICON: Record<string, string> = {
  excellent: "🚀", good: "✅", average: "⚠️", poor: "🔴",
};
const RATING_LABEL: Record<string, string> = {
  excellent: "Odlično", good: "Dobro", average: "Osrednje", poor: "Loše",
};
const PRIORITY_LABEL: Record<string, string> = {
  high: "Visok", medium: "Srednji", low: "Nizak",
};
const TREND_LABEL: Record<string, string> = {
  growth: "rast", decline: "pad", stable: "stabilan",
};

const fmt = (n: number, cur: string) =>
  `${Math.round(n || 0).toLocaleString("sr-RS")} ${cur}`;

const DashboardAIAnalysis: React.FC<AIAnalysisProps> = ({
  financeData, apartmentData, currency, locationName, locationId, period, periodKey,
}) => {
  const userData = useSelector((s: any) => s.userData.user);
  const superAdmin = isSuperAdmin(userData?.roles);
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [displayedText, setDisplayedText] = useState("");
  const [error, setError] = useState("");
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const cancelRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);
  const historySeqRef = useRef(0);

  // Chat state
  const [activeAnalysisId, setActiveAnalysisId] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatState, setChatState] = useState<"idle" | "confirming" | "loading">("idle");
  const [pendingQuestion, setPendingQuestion] = useState("");
  const chatBottomRef = useRef<HTMLDivElement>(null);
  // Typewriter: animate summary after result arrives
  useEffect(() => {
    if (!result?.summary) return;
    let i = 0;
    setDisplayedText("");
    const id = setInterval(() => {
      i++;
      setDisplayedText(result.summary.slice(0, i));
      if (i >= result.summary.length) clearInterval(id);
    }, 18);
    return () => clearInterval(id);
  }, [result?.summary]);

  // Reset state when period/location changes — user must click to generate
  useEffect(() => {
    setState("idle");
    setResult(null);
    setGeneratedAt(null);
    setError("");
    setActiveAnalysisId(null);
    setChatMessages([]);
    setChatInput("");
    setChatState("idle");
    setPendingQuestion("");
  }, [periodKey, locationId]);

  // Load persisted chat whenever activeAnalysisId is set
  useEffect(() => {
    if (!activeAnalysisId || !userData?.clientId) return;
    axios
      .get(`${BASE_API_URL}/${userData.clientId}/dashboard-ai-chat?analysisId=${activeAnalysisId}`, {
        headers: { "x-access-token": userData.accessToken || "" },
      })
      .then(({ data }) => {
        setChatMessages((data.chatMessages || []).map((m: any) => ({
          role: m.role as "user" | "assistant",
          content: m.content,
        })));
      })
      .catch(() => {});
  }, [activeAnalysisId]); // eslint-disable-line react-hooks/exhaustive-deps


  // Fetch history — initial load + refetch after each analysis completes.
  // Sequence number ensures a slow "loading-state" response never overwrites a newer one.
  useEffect(() => {
    if (!userData?.clientId || state === "loading") return;
    const seq = ++historySeqRef.current;
    axios
      .get(`${BASE_API_URL}/${userData.clientId}/dashboard-ai-history?limit=20`, {
        headers: { "x-access-token": userData.accessToken || "" },
      })
      .then(({ data }) => {
        if (seq === historySeqRef.current) setHistory(data?.history || []);
      })
      .catch(() => {});
  }, [userData?.clientId, state]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerate = useCallback(async () => {
    if (!userData?.clientId || !financeData) return;
    cancelRef.current?.cancel("Cancelled by user");
    const src = axios.CancelToken.source();
    cancelRef.current = src;

    setState("loading");
    setResult(null);
    setError("");

    try {
      // Strip detail/sub-arrays — backend builds its own context from summaries
      const trimmedFinance = financeData ? {
        ...financeData,
        months: (financeData.months || []).map(({ investicijeDetail: _a, kreditiDetail: _b, ...m }: any) => m),
        detail: financeData.detail ? {
          investments: financeData.detail.investments,
          credits:     financeData.detail.credits,
          rashod:      financeData.detail.rashod,
          profit:      financeData.detail.profit,
          finalProfit: financeData.detail.finalProfit,
        } : undefined,
      } : financeData;

      const { data } = await axios.post(
        `${BASE_API_URL}/${userData.clientId}/dashboard-ai-analysis`,
        { financeData: trimmedFinance, apartmentData, currency, locationName, locationId },
        {
          headers: { "x-access-token": userData.accessToken || "" },
          timeout: 50_000, // 50s client timeout (server kills at 45s)
          cancelToken: src.token,
        },
      );
      if (data?.analysis) {
        setResult(data.analysis);
        setGeneratedAt(new Date().toISOString());
        setActiveAnalysisId(data.analysisId || null);
        setChatMessages([]);
        setState("done");
      } else {
        throw new Error("Nevažeći format odgovora");
      }
    } catch (err: any) {
      if (axios.isCancel(err)) return;
      const msg = err.response?.data?.message || err.message || "Greška";
      setError(msg);
      setState("error");
    }
  }, [userData, financeData, apartmentData, currency, locationName]);

  const handleReset = () => {
    cancelRef.current?.cancel("Cancelled by user");
    setState("idle");
    setResult(null);
    setError("");
  };

  const handleChatSubmit = () => {
    const q = chatInput.trim();
    if (!q || chatState !== "idle") return;
    setPendingQuestion(q);
    setChatInput("");
    setChatState("confirming");
  };

  const handleChatConfirm = async () => {
    if (!pendingQuestion || !result) return;
    const question = pendingQuestion;
    setPendingQuestion("");
    setChatState("loading");

    const userMsg: ChatMessage = { role: "user", content: question };
    setChatMessages((prev) => [...prev, userMsg]);

    // Send full finance data so backend has payment methods, categories, investments, credits
    const trimmedFinance = financeData ? {
      ...financeData,
      months: (financeData.months || []).map(({ investicijeDetail: _a, kreditiDetail: _b, ...m }: any) => m),
    } : undefined;

    try {
      const { data } = await axios.post(
        `${BASE_API_URL}/${userData.clientId}/dashboard-ai-question`,
        {
          question,
          analysisJson: JSON.stringify(result),
          chatHistory: chatMessages,
          analysisId: activeAnalysisId,
          financeData: trimmedFinance,
          apartmentData,
          currency,
        },
        { headers: { "x-access-token": userData.accessToken || "" }, timeout: 35_000 },
      );
      setChatMessages((prev) => [...prev, { role: "assistant", content: data.answer }]);
    } catch (err: any) {
      const msg = err.response?.data?.message || "Greška pri slanju pitanja. Pokušaj ponovo.";
      setChatMessages((prev) => [...prev, { role: "assistant", content: `⚠️ ${msg}` }]);
    } finally {
      setChatState("idle");
    }
  };

  const handleChatCancel = () => {
    setChatInput(pendingQuestion);
    setPendingQuestion("");
    setChatState("idle");
  };

  const handleDeleteHistory = async (analysisId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm("Obriši ovu analizu i njen chat? Ova akcija je nepovratna.")) return;
    try {
      await axios.delete(
        `${BASE_API_URL}/${userData.clientId}/dashboard-ai-history/${analysisId}`,
        { headers: { "x-access-token": userData.accessToken || "" } },
      );
      setHistory((prev) => prev.filter((h) => h.analysisId !== analysisId));
      if (activeAnalysisId === analysisId) {
        setState("idle");
        setResult(null);
        setActiveAnalysisId(null);
        setChatMessages([]);
      }
    } catch {
      alert("Greška pri brisanju. Pokušaj ponovo.");
    }
  };

  return (
    <div className="ai-wrap">
      <div className="ai-header">
        <div className="ai-header-left">
          <span className="ai-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <path d="M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2z"/>
              <path d="M19 3l1 2 2 1-2 1-1 2-1-2-2-1 2-1z" opacity="0.5"/>
            </svg>
          </span>
          <div>
            <h3 className="ai-title">AI Analiza poslovanja</h3>
            <div className="ai-subtitle">{period}{locationName ? ` · ${locationName}` : ""}</div>
          </div>
        </div>
        <div className="ai-actions">
          {history.length > 0 && (
            <button className="ai-btn-ghost" onClick={() => setShowHistory((v) => !v)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>
              </svg>
              Historija ({history.length})
            </button>
          )}
          {state === "done" && (
            <button className="ai-btn-ghost" onClick={handleGenerate} disabled={!financeData}>
              Osvježi analizu
              <span className="ai-btn-cost">€0.50</span>
            </button>
          )}
          {state !== "loading" && state !== "done" && (
            <button className="ai-btn-primary" onClick={handleGenerate} disabled={!financeData}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2z"/>
              </svg>
              Generiši analizu
              <span className="ai-btn-cost">€0.50</span>
            </button>
          )}
          {state === "loading" && (
            <button className="ai-btn-ghost" onClick={() => { cancelRef.current?.cancel(); setState("idle"); }}>
              Otkaži
            </button>
          )}
        </div>
      </div>

      {/* ── History panel ────────────────────────────────────────────────── */}
      {showHistory && history.length > 0 && (
        <div className="ai-history">
          <div className="ai-history-head">
            Historija analiza
            <button className="ai-history-close" onClick={() => setShowHistory(false)}>✕</button>
          </div>
          <div className="ai-history-list">
            {history.map((h, i) => (
              <div key={i} className={`ai-history-row ${h.analysisId === activeAnalysisId ? "active" : ""}`}
                onClick={() => {
                  try {
                    setResult(normalizeAnalysis(JSON.parse(h.analysisJson)));
                    setGeneratedAt(h.generatedAt);
                    setActiveAnalysisId(h.analysisId); // triggers chat load via useEffect
                    setState("done");
                    setShowHistory(false);
                  } catch (_) {}
                }}>
                <div className="ai-history-score" style={{ color: SCORE_COLOR(h.score) }}>{h.score}</div>
                <div className="ai-history-body">
                  <div className="ai-history-period">
                    {h.periodLabel}{h.locationName ? ` · ${h.locationName}` : ""}
                    {h.analysisId === activeAnalysisId && <span className="ai-history-badge">Aktivan</span>}
                  </div>
                  <div className="ai-history-meta">
                    {RATING_ICON[h.ocjena] || "📊"} {RATING_LABEL[h.ocjena] || h.ocjena} · {dayjs(h.generatedAt).format("DD.MM.YYYY HH:mm")}
                    {h.chatCount > 0 && (
                      <span className="ai-history-chat-badge">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                        {h.chatCount}
                      </span>
                    )}
                  </div>
                  <div className="ai-history-sazetak">{h.kratakSazetak}</div>
                </div>
                {superAdmin && (
                  <button
                    className="ai-history-delete"
                    title="Obriši analizu"
                    onClick={(e) => handleDeleteHistory(h.analysisId, e)}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Idle placeholder ──────────────────────────────────────────────── */}
      {state === "idle" && (
        <div className="ai-placeholder">
          <div className="ai-placeholder-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
              <path d="M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2z"/>
              <circle cx="19" cy="5" r="2" opacity="0.4"/>
              <circle cx="5" cy="19" r="2" opacity="0.4"/>
            </svg>
          </div>
          <p>Klikni <strong>Generiši analizu</strong> da dobiješ AI pregled za period <strong>{period}</strong>.</p>
          <p className="ai-placeholder-sub">Claude analizira prihode, troškove, trend, investicije i daje konkretne preporuke za poboljšanje.</p>
        </div>
      )}

      {/* ── Loading / streaming ───────────────────────────────────────────── */}
      {state === "loading" && (
        <div className="ai-loading">
          <div className="ai-thinking">
            <span className="ai-dot" /><span className="ai-dot" /><span className="ai-dot" />
            <span className="ai-thinking-lbl">Claude analizira podatke… (do 45s)</span>
          </div>
          <div className="ai-loading-hint">
            Obrađujem prihode, troškove, trend i generiram preporuke.
          </div>
        </div>
      )}

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {state === "error" && (
        <div className="ai-error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/>
          </svg>
          {error || "Greška pri generisanju analize. Provjeri da li je AI dostupan."}
          <button className="ai-btn-ghost" style={{ marginLeft: 12 }} onClick={handleGenerate}>Pokušaj ponovo</button>
        </div>
      )}

      {/* ── Result ────────────────────────────────────────────────────────── */}
      {state === "done" && result && (
        <div className="ai-result">
          {/* Score + summary */}
          <div className="ai-score-row">
            <div className="ai-score-circle" style={{ "--score-color": SCORE_COLOR(result.score) } as any}>
              <svg viewBox="0 0 44 44" className="ai-score-svg">
                <circle cx="22" cy="22" r="18" fill="none" stroke="var(--ai-line)" strokeWidth="3"/>
                <circle cx="22" cy="22" r="18" fill="none"
                  stroke={SCORE_COLOR(result.score)} strokeWidth="3"
                  strokeDasharray={`${(result.score / 100) * 113} 113`}
                  strokeLinecap="round" transform="rotate(-90 22 22)"/>
              </svg>
              <div className="ai-score-inner">
                <span className="ai-score-num">{result.score}</span>
                <span className="ai-score-label">/ 100</span>
              </div>
            </div>
            <div className="ai-score-text">
              <div className="ai-ocjena">
                {RATING_ICON[result.rating] || "📊"} {RATING_LABEL[result.rating] || result.rating}
                <span className={`ai-trend-chip ${result.trend}`}>
                  {result.trend === "growth" ? "▲" : result.trend === "decline" ? "▼" : "→"} {TREND_LABEL[result.trend] || result.trend}
                </span>
              </div>
              <p className="ai-sazetak">{displayedText}<span className="ai-cursor" style={{ opacity: displayedText.length < (result.summary?.length ?? 0) ? 1 : 0 }}>▌</span></p>
              <p className="ai-trend-kom">{result.trendComment}</p>
            </div>
          </div>

          <div className="ai-sections">
            {/* Positives */}
            {(result.positives?.length ?? 0) > 0 && (
              <div className="ai-section">
                <div className="ai-section-head ok">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                  Dobro
                </div>
                {(result.positives ?? []).map((p, i) => (
                  <div key={i} className="ai-item ok">
                    <span className="ai-item-title">{p.title}</span>
                    <span className="ai-item-desc">{p.description}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Warnings */}
            {(result.warnings?.length ?? 0) > 0 && (
              <div className="ai-section">
                <div className="ai-section-head warn">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  Upozorenja
                </div>
                {(result.warnings ?? []).map((w, i) => (
                  <div key={i} className="ai-item warn">
                    <span className="ai-item-title">{w.title}</span>
                    <span className="ai-item-desc">{w.description}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Key expenses */}
            {(result.keyExpenses?.length ?? 0) > 0 && (
              <div className="ai-section">
                <div className="ai-section-head rose">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h12"/></svg>
                  Gdje odlazi novac
                </div>
                {(result.keyExpenses ?? []).map((k, i) => (
                  <div key={i} className="ai-cost-row">
                    <div className="ai-cost-left">
                      <span className="ai-cost-name">{k.category}</span>
                      <span className="ai-cost-comment">{k.comment}</span>
                    </div>
                    <div className="ai-cost-right">
                      <div className="ai-cost-bar">
                        <div className="ai-cost-fill" style={{ width: `${Math.min(k.percentage, 100)}%` }} />
                      </div>
                      <span className="ai-cost-pct">{k.percentage}%</span>
                      <span className="ai-cost-val">{fmt(k.amount, currency)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Recommendations */}
            {(result.recommendations?.length ?? 0) > 0 && (
              <div className="ai-section">
                <div className="ai-section-head accent">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                  Preporuke
                </div>
                {(result.recommendations ?? []).map((p, i) => (
                  <div key={i} className={`ai-prec ai-prec-${p.priority}`}>
                    <span className="ai-prec-badge">{PRIORITY_LABEL[p.priority]}</span>
                    <div className="ai-prec-body">
                      <span className="ai-prec-akcija">{p.action}</span>
                      <span className="ai-prec-efekt">→ {p.effect}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="ai-footer">
            Generisano Claude AI{generatedAt ? ` · ${dayjs(generatedAt).format("DD.MM.YYYY u HH:mm")}` : ""} · Analiza je informativnog karaktera.
          </div>

          {/* ── Chat / follow-up questions ──────────────────────────── */}
          <div className="ai-chat">
            <div className="ai-chat-head">
              <div className="ai-chat-head-left">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                Postavi pitanje
              </div>
              <span className="ai-chat-cost">€0.30 / pitanje</span>
            </div>

            {/* Messages */}
            {chatMessages.length > 0 && (
              <div className="ai-chat-messages">
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`ai-chat-msg ai-chat-msg-${msg.role}`}>
                    {msg.role === "assistant" && (
                      <span className="ai-chat-avatar">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2z"/>
                        </svg>
                      </span>
                    )}
                    <span className="ai-chat-bubble">{msg.content}</span>
                  </div>
                ))}
                {chatState === "loading" && (
                  <div className="ai-chat-msg ai-chat-msg-assistant">
                    <span className="ai-chat-avatar">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2z"/>
                      </svg>
                    </span>
                    <span className="ai-chat-bubble ai-chat-bubble-loading">
                      <span className="ai-dot"/><span className="ai-dot"/><span className="ai-dot"/>
                    </span>
                  </div>
                )}
                <div ref={chatBottomRef} />
              </div>
            )}

            {/* Hints — always visible above input */}
            <div className="ai-chat-hints">
              <span className="ai-chat-hints-label">Brzi odabir:</span>
              {[
                "Koji način plaćanja donosi najviše prihoda?",
                "Koliko iznosi Beli keš u ovom periodu?",
                "Koji su direktni troškovi i koji je najveći?",
                "Koji operativni trošak je najveći?",
                "Koliki je udio troškova u ukupnom prihodu?",
                "Koji mesec ima najgoru maržu i zašto?",
                "Kakav je trend prihoda — raste ili pada?",
                "U kom mesecu je bio najveći profit?",
                "Kako investicije utiču na finalni profit?",
                "Koji kredit ima najveću ratu ovaj mesec?",
                "Koliko rata kredita još preostaje?",
                "Zašto su troškovi porasli u poslednjem mesecu?",
                "Koliki je prosječni mesečni prihod u periodu?",
                "Šta je potrebno da dostignemo pozitivnu maržu?",
                "Postoji li sezonalnost u prihodima?",
              ].map((q) => (
                <button
                  key={q}
                  className="ai-chat-hint-chip"
                  disabled={chatState === "loading" || chatState === "confirming"}
                  onClick={() => setChatInput(q)}
                >
                  {q}
                </button>
              ))}
            </div>

            {/* Confirmation bar */}
            {chatState === "confirming" && (
              <div className="ai-chat-confirm">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/>
                </svg>
                <span>Ovo pitanje košta <strong>€0.30</strong>. Nastavi?</span>
                <div className="ai-chat-confirm-actions">
                  <button className="ai-chat-confirm-yes" onClick={handleChatConfirm}>
                    Potvrdi · €0.30
                  </button>
                  <button className="ai-chat-confirm-no" onClick={handleChatCancel}>
                    Otkaži
                  </button>
                </div>
              </div>
            )}

            {/* Input row */}
            {chatState !== "confirming" && (
              <div className="ai-chat-input-row">
                <input
                  className="ai-chat-input"
                  type="text"
                  placeholder="Npr. Zašto su troškovi porasli u aprilu?"
                  value={chatInput}
                  disabled={chatState === "loading"}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleChatSubmit(); } }}
                  maxLength={500}
                />
                <button
                  className="ai-chat-send"
                  onClick={handleChatSubmit}
                  disabled={!chatInput.trim() || chatState === "loading"}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M22 2L11 13M22 2L15 22l-4-9-9-4z"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardAIAnalysis;
